import React from "react";
import { Phone, ChevronDown, MapPin, CheckCircle, PhoneCall } from "lucide-react";
import { Button } from "./ui/button";
import ResponsiveImage from "./ResponsiveImage";
import StarRating from "./StarRating";
import { companyInfo } from "../data/mock";
import { images } from "../data/images";

const bullets = [
  "Volledig verzekerd & KvK-geregistreerd",
  `${companyInfo.rating}★ uit ${companyInfo.reviewCount} Google reviews`,
  "Prijs vooraf, geen verrassingen achteraf",
];

const Hero = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Welkom bij Cleven Cleaning"
    >
      <div className="absolute inset-0">
        <ResponsiveImage
          image={images.hero}
          alt="Felix Cleven - Professionele glazenwasser in Rosmalen, Den Bosch en omstreken aan het werk"
          sizes="100vw"
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-sky-900/70 to-teal-900/70" />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <div className="text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-8 border border-white/20">
            <MapPin className="w-4 h-4 text-sky-400" />
            <span className="text-white/90 text-sm font-medium">{companyInfo.location}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            Glazenwasser in Rosmalen &amp; Den Bosch
            <span className="block mt-2 bg-gradient-to-r from-sky-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Streeploos schoon, prijs in 60 seconden
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-6">
            Ramen, kozijnen, zonnepanelen, dakgoten en hogedrukreiniging voor woningen en bedrijven.
            U krijgt altijd 24 uur vooraf bericht en een prijs die klopt.
          </p>

          <ul className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6" data-testid="hero-bullets">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-2 text-white/90 text-sm sm:text-base">
                <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <p className="text-sm sm:text-base text-white/60 max-w-2xl mx-auto mb-8" data-testid="hero-service-areas">
            Werkgebied: Rosmalen &middot; &apos;s&#8209;Hertogenbosch &middot; Vught &middot; Berlicum &middot; Heesch &middot; Nuland &middot; Uden &middot; Empel
          </p>

          <div className="flex items-center justify-center space-x-2 mb-10" data-testid="hero-rating">
            <StarRating rating={companyInfo.rating} />
            <span className="text-white font-semibold">{companyInfo.rating}</span>
            <span className="text-white/60">({companyInfo.reviewCount} reviews op Google)</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-5">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 transition-all hover:scale-105"
            >
              <a href="#calculator" data-testid="hero-cta-calculator">Bereken direct uw prijs</a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white/30 bg-white/10 backdrop-blur-md text-white hover:bg-white/20 hover:border-white/50 text-lg px-8 py-6 rounded-xl transition-all"
            >
              <a href={`tel:${companyInfo.phoneClean}`} className="flex items-center space-x-2" data-testid="hero-cta-phone">
                <Phone className="w-5 h-5" />
                <span>{companyInfo.phone}</span>
              </a>
            </Button>
          </div>

          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors mb-14"
            data-testid="hero-callback-link"
          >
            <PhoneCall className="w-4 h-4" />
            Liever teruggebeld worden? Laat uw nummer achter
          </a>

          <div>
            <a href="#diensten" className="inline-flex flex-col items-center text-white/60 hover:text-white transition-colors">
              <span className="text-sm mb-2">Ontdek onze diensten</span>
              <ChevronDown className="w-6 h-6 animate-bounce" />
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
